import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ChatUserSelectionEdit } from 'src/app/chat/chat-user-selection/model/chat-user-selection';
import { ChatHistoryPageRequest, ChatHistoryPageResponse } from 'src/app/chat/model/chat-history-page';
import { ChatMessageReaction } from 'src/app/chat/model/chat-message-reaction';

@Injectable({
  providedIn: 'root'
})
export class ChatService
{
  constructor(private http: HttpClient) { }

  /**
   * Add a chat or space.
   *
   * @param data Facility to persit
   * @param isNew Indicate if this is a new or update
   */
  public updateChat(data: any, isNew: boolean, id?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/chat', data);
    }
    else
    {
      return this.http.put(`/chat/${id}`, data);
    }
  }

  /**
   * Get all chat for the current logged-in user.
   *
   * @returns List of user chat.
   */
  public getUserChat(): Observable<any[]>
  {
    return this.http.get<any[]>(`/chat`);
  }

  /**
   * Get chat by id.
   *
   * @param id Chat id
   */
  public getChatById(id: number): Observable<ChatUserSelectionEdit>
  {
    return this.http.get<ChatUserSelectionEdit>(`/chat/${id}`);
  }

  /**
   * Add chat history
   *
   * @param chatId Current chat id
   * @param data Chat data
   */
  public addChatHistory(chatId: number, data: any): Observable<any>
  {
    return this.http.post(`/chat/${chatId}/history`, data);
  }

  /**
   * Notify other chat members that the current user is typing or has stopped.
   */
  public sendChatTyping(chatId: number, typing: boolean): Observable<void>
  {
    return this.http.post<void>(`/chat/${chatId}/typing`, { typing });
  }

  /**
   * Delete chat history
   *
   * @param chatHistoryId Chat history id to delete
   */
  public deleteChatHistory(chatHistoryId: number): Observable<any>
  {
    return this.http.delete(`/chat/history/${chatHistoryId}`);
  }

  /**
   * Add or remove the current user's reaction on a chat history message.
   *
   * @param chatHistoryId Chat history message id
   * @param emojiKey Emoji identity key (e.g. id:ready-computing-heart or char:👍)
   */
  public toggleChatHistoryReaction(chatHistoryId: number, emojiKey: string): Observable<ChatMessageReaction[]>
  {
    return this.http.put<ChatMessageReaction[]>(`/chat/history/${chatHistoryId}/reaction`, { emojiKey });
  }

  /**
   * Get all chat history (legacy; prefer {@link getChatHistoryPage}).
   *
   * @returns List of user chat history.
   */
  public getChatHistory(chatId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/chat/${chatId}/history`);
  }

  /**
   * Paginated chat history: latest page when {@code beforeId} is omitted; older page when set.
   */
  public getChatHistoryPage(chatId: number, request: ChatHistoryPageRequest): Observable<ChatHistoryPageResponse>
  {
    let params = new HttpParams().set('limit', String(request.limit));
    if (request.beforeId != null)
    {
      params = params.set('beforeId', String(request.beforeId));
    }

    return this.http.get<ChatHistoryPageResponse>(`/chat/${chatId}/history`, { params });
  }

  public setUnread(chatUserId: number, newValue: string, notify = true): Observable<any>
  {
    return this.http.put(`/chat/user/${chatUserId}/unread/${newValue}?notify=${notify}`, null);
  }

  public setPinned(chatUserId: number, newValue: string): Observable<any>
  {
    return this.http.put(`/chat/user/${chatUserId}/pinned/${newValue}`, null);
  }

  public leaveChat(chatUserId: number): Observable<any>
  {
    return this.http.put(`/chat/user/${chatUserId}/leave`, null);
  }

  /**
   * Get count chat by user and status.
   */
  public getCountChatByChat(userId: number): Observable<number>
  {
    return this.http.get<number>(`/chat/count/${userId}`);
  }

  /**
   * Check if Chat Name exists to avoid name duplicates
   *
   * @param name Chat Name
   */
  public existsChatSpaceName(name: string): Observable<any>
  {
    return this.http.get(`/chat/space/exists?name=${name}`);
  }
}
